import {
    useCallback, useMemo,
} from 'react';
import moment from 'moment/moment';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';

export const useContactGroupOwners = ({
    connectionGroupId,
    clientId,
}: {
    connectionGroupId?: number | undefined;
    clientId?: number | undefined;
}) => {
    const {
        data: contactGroupDetails,
    } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroupDetails',
        payload: { groupId: connectionGroupId },
        enabled: !!connectionGroupId,
    });

    const owners = useMemo(
        () => (contactGroupDetails?.data?.owners
            ? Object.keys(contactGroupDetails.data.owners)?.map((key) => ({
                id: parseInt(key, 10),
                name: contactGroupDetails.data.owners[key],
            }))
            : undefined),
        [contactGroupDetails?.data?.owners],
    );

    const sortedOwners = useMemo(
        () => owners?.sort((a, b) => a.id - b.id),
        [owners],
    );

    const mainOwner = useMemo(
        () => sortedOwners?.[0],
        [sortedOwners],
    );

    const jointAccountOwner = useMemo(
        () => sortedOwners?.[1],
        [sortedOwners],
    );

    const {
        data: mainOwnerData,
        error: mainOwnerError,
        isLoading: isMainOwnerLoading,
        refetch: getMainOwnerData,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getContact',
        payload: mainOwner?.id || clientId,
        enabled: !!mainOwner?.id || !!clientId,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60,
    });

    const {
        data: jointAccountOwnerData,
        error: jointAccountOwnerError,
        isLoading: isJointAccountOwnerLoading,
        refetch: getJointAccountOwnerData,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getContact',
        payload: jointAccountOwner?.id,
        enabled: !!connectionGroupId && !!jointAccountOwner?.id,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60,
    });

    const mainOwnerWithAge = useMemo(() => {
        if (!mainOwner && !mainOwnerData?.data) {
            return null;
        }
        const userClientAge = moment(
            moment().format('YYYY-MM-DD'),
        ).diff(
            moment(
                mainOwnerData?.data?.details?.personalInformation?.dateOfBirth,
            ),
            'years',
            true,
        );

        return {
            ...mainOwner,
            name: mainOwner?.name || mainOwnerData?.data?.details?.personalInformation?.displayName,
            age: mainOwnerData?.data?.details?.personalInformation?.dateOfBirth ? String(Math.floor(userClientAge)) : null,
            zipCode: mainOwnerData?.data?.details?.personalInformation?.primaryAddressPostalCode,
            city: mainOwnerData?.data?.details?.personalInformation?.primaryAddressCity,
            clientData: mainOwnerData?.data,
        };
    }, [mainOwnerData, mainOwner]);

    const jointAccountOwnerWithAge = useMemo(() => {
        if (!jointAccountOwner && !jointAccountOwnerData?.data) {
            return null;
        }
        const userClientAge = moment(
            moment().format('YYYY-MM-DD'),
        ).diff(
            moment(
                jointAccountOwnerData?.data?.details?.personalInformation?.dateOfBirth,
            ),
            'years',
            true,
        );

        return {
            ...jointAccountOwner,
            name: jointAccountOwner?.name || jointAccountOwnerData?.data?.details?.personalInformation?.displayName,
            age: jointAccountOwnerData?.data?.details?.personalInformation?.dateOfBirth ? String(Math.floor(userClientAge)) : null,
            zipCode: jointAccountOwnerData?.data?.details?.personalInformation?.primaryAddressPostalCode,
            city: jointAccountOwnerData?.data?.details?.personalInformation?.primaryAddressCity,
            clientData: jointAccountOwnerData?.data,
        };
    }, [jointAccountOwnerData, jointAccountOwner]);

    const fetchClientData = useCallback(async () => {
        getMainOwnerData();
        if (jointAccountOwner?.id) {
            getJointAccountOwnerData();
        }
    }, [getMainOwnerData, getJointAccountOwnerData, jointAccountOwner?.id]);

    return {
        mainOwner: mainOwnerWithAge,
        jaClient: jointAccountOwnerWithAge,
        isLoading: isMainOwnerLoading || isJointAccountOwnerLoading,
        error: mainOwnerError || jointAccountOwnerError,
        refetchMainOwnerData: getMainOwnerData,
        refetchJointAccountOwnerData: getJointAccountOwnerData,
        fetchClientData,
    };
};
